import React, { useState } from 'react';
import { supabase } from './supabaseClient';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import { GlobalWorkerOptions } from 'pdfjs-dist/build/pdf';

GlobalWorkerOptions.workerSrc = '//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';

function SubmitPdf() {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [step, setStep] = useState(1);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfText, setPdfText] = useState('');
  const [extracted, setExtracted] = useState(null); // { title, authors, journal, year, abstract }
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Real PDF text extraction using pdfjs-dist
  const extractPdfText = async (file) => {
    if (!file.type || file.type !== 'application/pdf') throw new Error('File is not a PDF.');
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async function() {
        const typedarray = new Uint8Array(this.result);
        try {
          const pdf = await pdfjsLib.getDocument({ data: typedarray }).promise;
          let text = '';
          for (let i = 1; i <= Math.min(pdf.numPages, 2); i++) { // Only first 2 pages for speed
            const page = await pdf.getPage(i);
            const content = await page.getTextContent();
            text += content.items.map(item => item.str).join(' ') + '\n';
          }
          resolve(text);
        } catch (err) {
          console.error('PDF extraction error:', err);
          reject(err);
        }
      };
      reader.onerror = (err) => {
        console.error('FileReader error:', err);
        reject(err);
      };
      reader.readAsArrayBuffer(file);
    });
  };

  // Template-specific extraction for journal, title, authors, abstract, keywords
  const extractFields = (text) => {
    const lines = text.split(/\n|\r/).map(l => l.trim()).filter(Boolean);
    let journal = '', title = '', authors = '', abstract = '', keywords = '', year = '';
    // 1. Journal
    for (let l of lines) {
      if (/journal of biotechnological sciences/i.test(l)) {
        journal = 'JBS';
        break;
      }
      if (/journal of natural sciences/i.test(l)) {
        journal = 'JNS';
        break;
      }
    }
    // 2. Title: longest line between journal and ABSTRACT
    let journalIdx = lines.findIndex(l => /journal of (biotechnological sciences|natural sciences)/i.test(l));
    let abstractIdx = lines.findIndex(l => /abstract/i.test(l));
    let titleCandidates = lines.slice(journalIdx + 1, abstractIdx > 0 ? abstractIdx : journalIdx + 8);
    title = titleCandidates.reduce((longest, l) => (l.length > longest.length ? l : longest), '');
    // 3. Authors: line directly under title
    let titleIdx = lines.findIndex(l => l === title);
    if (titleIdx !== -1 && titleIdx + 1 < lines.length) {
      authors = lines[titleIdx + 1];
    }
    // 4. Abstract: lines after 'ABSTRACT' up to 'Key words' or 'Keywords'
    if (abstractIdx !== -1) {
      let keyIdx = lines.findIndex((l, i) => i > abstractIdx && /key ?words?/i.test(l));
      if (keyIdx !== -1) {
        abstract = lines.slice(abstractIdx + 1, keyIdx).join(' ');
      } else {
        abstract = lines.slice(abstractIdx + 1, abstractIdx + 7).join(' ');
      }
    }
    // 5. Keywords: line after 'Key words:' or 'Keywords:'
    let kwIdx = lines.findIndex(l => /key ?words?:/i.test(l));
    if (kwIdx !== -1 && kwIdx + 1 < lines.length) {
      keywords = lines[kwIdx].replace(/key ?words?:/i, '').trim();
      if (!keywords && lines[kwIdx + 1]) {
        keywords = lines[kwIdx + 1];
      }
    }
    // 6. Year: first 4-digit year
    const yearMatch = text.match(/\b(19|20)\d{2}\b/);
    if (yearMatch) year = yearMatch[0];
    return { title, authors, journal, year, abstract, keywords };
  };

  const handlePdfChange = async (e) => {
    setError('');
    const file = e.target.files[0];
    if (!file) return;
    setPdfFile(file);
    setLoading(true);
    try {
      const text = await extractPdfText(file);
      setPdfText(text);
      const fields = extractFields(text);
      setExtracted(fields);
      setStep(2);
    } catch (err) {
      setError('Failed to extract text from PDF.');
      console.error('Failed to extract text from PDF:', err);
    }
    setLoading(false);
  };

  // Step 2: Show form with extracted data
  const [form, setForm] = useState({ title: '', authors: '', journal: '', year: '', abstract: '', keywords: '' });
  React.useEffect(() => {
    if (extracted) {
      setForm({
        title: extracted.title || '',
        authors: extracted.authors || '',
        journal: extracted.journal || '',
        year: extracted.year || '',
        abstract: extracted.abstract || '',
        keywords: extracted.keywords || '',
      });
    }
  }, [extracted]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    // Upload PDF to Supabase Storage
    let pdf_url = '';
    if (pdfFile) {
      const fileExt = pdfFile.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      const { data, error: uploadError } = await supabase.storage.from('pdf').upload(fileName, pdfFile);
      if (uploadError) {
        setError('Failed to upload PDF.');
        setSubmitting(false);
        return;
      }
      const { data: urlData } = supabase.storage.from('pdf').getPublicUrl(fileName);
      pdf_url = urlData.publicUrl;
    }
    // Insert into request table
    const { error: insertError } = await supabase.from('request').insert([
      { ...form, username: user.username || user.full_name || user.email, email: user.email, pdf_url }
    ]);
    if (insertError) {
      setError('Failed to submit publication. ' + (insertError.message || ''));
      setSubmitting(false);
      return;
    }
    setSubmitted(true);
    setSubmitting(false);
  };

  return (
    <div className="container mt-5" style={{maxWidth: 700}}>
      <div className="card shadow-sm border-0">
        <div className="card-body">
          <h2 className="mb-4 text-center">Submit by PDF Upload</h2>
          {step === 1 && !submitted && (
            <>
              <div className="mb-3">
                <label className="form-label">Upload PDF File</label>
                <input type="file" className="form-control" accept="application/pdf" onChange={handlePdfChange} />
              </div>
              {loading && <div className="alert alert-info">Extracting data from PDF...</div>}
              {error && <div className="alert alert-danger">{error}</div>}
            </>
          )}
          {step === 2 && !submitted && (
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Title</label>
                <input type="text" className="form-control" name="title" value={form.title} onChange={handleChange} required />
              </div>
              <div className="mb-3">
                <label className="form-label">Authors</label>
                <input type="text" className="form-control" name="authors" value={form.authors} onChange={handleChange} required />
              </div>
              <div className="mb-3">
                <label className="form-label">Journal</label>
                <input type="text" className="form-control" name="journal" value={form.journal} onChange={handleChange} />
              </div>
              <div className="mb-3">
                <label className="form-label">Year</label>
                <input type="text" className="form-control" name="year" value={form.year} onChange={handleChange} />
              </div>
              <div className="mb-3">
                <label className="form-label">Abstract</label>
                <textarea className="form-control" name="abstract" rows="3" value={form.abstract} onChange={handleChange}></textarea>
              </div>
              <div className="mb-3">
                <label className="form-label">Keywords</label>
                <input type="text" className="form-control" name="keywords" value={form.keywords} onChange={handleChange} />
              </div>
              {submitting && <div className="alert alert-info text-center">Submitting...</div>}
              {error && <div className="alert alert-danger text-center">{error}</div>}
              <div className="d-grid">
                <button type="submit" className="btn btn-success">Submit Publication</button>
              </div>
            </form>
          )}
          {submitted && <div className="alert alert-success text-center">Your submission has been received!</div>}
        </div>
      </div>
    </div>
  );
}

export default SubmitPdf; 